import { motion } from "framer-motion";
import {
  LayoutDashboard, CalendarDays, Users2, ReceiptText, PieChart,
  BrainCircuit, ChevronRight, Sparkles, TrendingUp, AlertTriangle,
} from "lucide-react";

/**
 * Hero right — Premium floating dashboard mockup for the Executive AI landing.
 * All pure CSS/SVG (no screenshots). Designed for the dark-teal + gold palette.
 */
export default function ExecHeroDashboard() {
  return (
   <div
      className="relative w-full aspect-[10/9] max-w-[820px] ml-auto"
      style={{
        perspective: "1400px",
        perspectiveOrigin: "right center"
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute -inset-8 -z-10 pointer-events-none"
          style={{
            background: `
              radial-gradient(circle at 50% 50%,
                rgba(8, 76, 82, 0.87),
                rgba(10,31,39,.35) 45%,
                transparent 75%
              ),
              radial-gradient(circle,
                rgba(212,160,23,.05),
                transparent 80%
              )
            `,
            filter: "blur(42px)"
          }}
      />

      {/* Card frame */}
      <div
        className="relative w-full h-full exec-card overflow-hidden"
        style={{
            transform:
              "rotateY(-14deg) rotateX(5deg) rotateZ(-1deg) scale(1)",
            transformStyle:"preserve-3d",
            boxShadow:
              "0 60px 120px rgba(0,0,0,.55), 0 0 140px rgba(5, 73, 58, 0.22)"
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-exec-gold" />
         <span className="font-display text-[13px] tracking-tight text-[#D4A017]">
              VELQUE
         </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-white/10" />
            <span className="w-2 h-2 rounded-full bg-white/10" />
            <span className="w-2 h-2 rounded-full bg-exec-emerald/50" />
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-[120px_minmax(0,1fr)] h-[calc(100%-56px)]">
          {/* Sidebar */}
          <div className="border-r border-white/[0.08] px-2 py-4 flex flex-col gap-1">
            {[
              { icon: LayoutDashboard, l: "AI Executive Overview", active: true },
              { icon: CalendarDays, l: "Appointments" },
              { icon: ReceiptText, l: "Vendors & Invoice" },
              { icon: PieChart, l: "Finance & Profitability" },
              { icon: BrainCircuit, l: "Business Settings" },
            ].map((it) => (
              <div
                key={it.l}
                className={`flex items-center gap-2 px-2.5 h-8 rounded-xl text-[10.5px] ${
                  it.active
                    ? "bg-exec-emerald-3/40 text-exec-emerald-2 border border-exec-emerald/25"
                    : "text-exec-mute"
                }`}
              >
                <it.icon className="w-2.5 h-2.5" strokeWidth={1.5} />
               <span className="truncate whitespace-nowrap">
                 {it.l}
               </span>
              </div>
            ))}
            <div className="mt-auto pt-3 border-t border-white/[0.08] flex items-center gap-2 px-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-exec-gold to-exec-gold-3 grid place-items-center text-[10px] font-semibold text-[#0B2529]">S</div>
              <div className="min-w-0">
                <div className="text-[11px] text-exec-ink truncate">Elite Smile</div>
                <div className="text-[9.5px] text-exec-mute-2 truncate">Founder</div>
              </div>
            </div>
          </div>

          {/* Main */}
          <div className="p-5 overflow-hidden">
            {/* Greeting */}
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3 h-3 text-exec-gold-2" strokeWidth={1.5} />
              <span className="text-[10.5px] tracking-[0.22em] uppercase text-exec-emerald-2 font-medium">Executive Brief</span>
            </div>
            <div className="font-display text-[19px] leading-tight text-exec-ink">
              Good morning, <span className="italic text-exec-gold-2">Elite Smile</span>
            </div>
            <div className="text-[11.5px] text-exec-mute mt-1">
              I&apos;ve prepared your business day — 3 priorities need attention.
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-2 mt-4">
              {[
                { l: "Appointments", v: "24", d: "2 VIP" },
                { l: "Vendor Due",  v: "₹2.45L", d: "8 pending", warn: true },
                { l: "Cash Health", v: "Healthy", d: "+18% MoM", ok: true },
              ].map((k) => (
                <div key={k.l} className="rounded-2xl bg-white/[0.035] border border-white/[0.08] p-3">
                  <div className="text-[9.5px] tracking-[0.12em] uppercase text-exec-mute-2 font-medium">{k.l}</div>
                  <div className={`mt-1.5 text-[16px] font-medium ${k.ok ? "text-exec-emerald-2" : k.warn ? "text-exec-gold-2" : "text-exec-ink"}`}>
                    {k.v}
                  </div>
                  <div className="text-[10px] text-exec-mute mt-0.5">{k.d}</div>
                </div>
              ))}
            </div>

            {/* Trend + Priorities */}
            <div className="grid grid-cols-[1.2fr_1fr] gap-2.5 mt-3">
              <div className="rounded-2xl bg-white/[0.035] border border-white/[0.08] p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.12em] uppercase text-exec-mute-2 font-medium">Revenue · 30d</div>
                  <div className="flex items-center gap-1 text-[10px] text-exec-emerald-2"><TrendingUp className="w-3 h-3" strokeWidth={1.6}/> +18.2%</div>
                </div>
                <Sparkline />
              </div>
              <div className="rounded-2xl bg-white/[0.02] border border-white/[0.08] p-3">
                <div className="text-[10px] tracking-[0.12em] uppercase text-exec-mute-2 font-medium mb-2">Today&apos;s Priorities</div>
                <ul className="space-y-1.5">
                  {[
                    { l: "Confirm Priya · 11:00", ok: true },
                    { l: "Pay ABC Traders · ₹18K", warn: true },
                    { l: "Follow up: Neha (30d)", ok: true },
                  ].map((p, i) => (
                    <li key={i} className="flex items-center gap-2 text-[11px] text-exec-ink-2">
                      <span className={`w-1 h-1 rounded-full ${p.warn ? "bg-exec-gold" : "bg-exec-emerald"}`}/> {p.l}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* AI recommendation strip */}
            <div className="mt-3 rounded-2xl border border-exec-emerald/25 bg-exec-emerald-3/25 px-3 py-2.5 flex items-center gap-2.5">
              <div className="w-6 h-6 rounded-full bg-exec-emerald/20 grid place-items-center">
                <Sparkles className="w-3 h-3 text-exec-emerald-2" strokeWidth={1.6}/>
              </div>
              <div className="text-[11px] text-exec-ink-2 leading-snug flex-1">
                <span className="text-exec-emerald-2 font-medium">Nexa: </span>
                Send Neha a welcome-back offer — she was your top customer last quarter.
              </div>
              <ChevronRight className="w-3 h-3 text-exec-mute" strokeWidth={1.5}/>
            </div>
          </div>
        </div>
      </div>

      {/* AI Orb */}
      <AIOrb />   

      {/* Floating side badges */}
      <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="absolute right-[25px] top-[58px] exec-card-solid px-3 py-2.5 flex items-center gap-2 exec-float-slow z-50"
          style={{
            boxShadow:
              "0 0 8px rgba(205, 151, 15, 0.12), 0 10px 24px rgba(0,0,0,.32)"
          }}
      >
        <div className="w-5 h-5 rounded-full bg-exec-gold/15 grid place-items-center">
          <AlertTriangle className="w-3 h-3 text-exec-gold-2" strokeWidth={1.7}/>
        </div>
        <div className="text-[11px]">
          <div className="text-exec-ink font-medium">3 invoices overdue</div>
          <div className="text-exec-mute-2">tap to review</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.75 }}
        className="absolute left-[66%] bottom-1 max-w-[165px] exec-card-solid px-3 py-2.5 flex items-center gap-2 exec-float-med"
        style={{
          boxShadow:
            "0 12px 30px rgba(0,0,0,.30)"
        }}
      >
        <div className="w-5 h-5 rounded-full bg-exec-emerald/15 grid place-items-center">
          <TrendingUp className="w-3 h-3 text-exec-emerald-2" strokeWidth={1.7}/>
        </div>
        <div className="text-[11px]">
          <div className="text-exec-ink font-medium">Cash flow healthy</div>
          <div className="text-exec-mute-2">+₹42K vs last week</div>
        </div>
      </motion.div>
    </div>
  );
}

/* Tiny inline sparkline SVG */
function Sparkline() {
  const points = [24, 28, 22, 30, 26, 34, 30, 38, 34, 42, 40, 48, 44, 52];
  const max = Math.max(...points), min = Math.min(...points);
  const w = 250, h = 42;
  const step = w / (points.length - 1);
  const y = (v) => h - ((v - min) / (max - min)) * (h - 6) - 3;
  const d = points.map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  const area = d + ` L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[52px] mt-2">
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2FCBA9" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#2FCBA9" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkFill)" />
      <path d={d} fill="none" stroke="#63E4C7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AIOrb() {
  return (
    <motion.div
      animate={{
        y: [0, -6, 0],
        scale: [1, 1.02, 1],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="absolute right-[-8px] top-[42%] z-40"

    >
      <div
        className="relative w-[60px] h-[60px] rounded-full"
        style={{
          background:
            "radial-gradient(circle at 28% 25%, #5d5d5d 0%, #2e2e2e 25%, #111 55%, #000 100%)",
          boxShadow:
            "0 0 8px rgba(255,190,70,.28), 0 0 18px rgba(255,190,70,.10)"
        }}
      >
        {/* Top reflection */}
        <div
          className="absolute left-[8px] top-[8px] w-[22px] h-[10px] rounded-full opacity-60"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255,255,255,.55), transparent)",
            filter: "blur(2px)",
          }}
        />

        {/* Eyes */}
        <div className="absolute left-[8px] top-[21px] flex gap-2">
          <div
            className="w-[9px] h-[9px] rounded-full"
            style={{
              background: "#FFC94D",
              boxShadow: "0 0 10px #FFC94D",
            }}
          />

          <div
            className="w-[8px] h-[8px] rounded-full"
            style={{
              background: "#FFC94D",
              boxShadow: "0 0 10px #FFC94D",
            }}
          />
        </div>

        {/* Side Ear */}
        <div
          className="absolute right-[-5px] top-[25px] w-[8px] h-[20px] rounded-full"
          style={{
            background:
              "linear-gradient(90deg,#565656,#1d1d1d)",
            border: "1px solid rgba(255,210,90,.20)",
          }}
        />
      </div>
    </motion.div>
  );
}
